import React, { useMemo, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Box, VStack, IconButton, Flex, Text, Tooltip } from "@chakra-ui/react";
import {
  MdOutlineHome,
  MdOutlineRocketLaunch,
  MdOutlinePeople,
  MdOutlineMenu,
  MdOutlinePerson,
  MdOutlineImage,
  MdOutlinePark,
  MdOutlineCategory,
  MdOutlineLocalOffer,
  MdOutlineKey,
  MdHome,
  MdRocketLaunch,
  MdPeople,
  MdPerson,
  MdImage,
  MdPark,
  MdCategory,
  MdLocalOffer,
  MdKey,
} from "react-icons/md";
import { ArawaksLogo } from "components/utils/ArawaksLogo";
import { useDashboardSettings } from "features/dashboard/settings/store";
import { getDashboardPath } from "features/dashboard/portal";

const labelStyle = {
  fontSize: "10px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const Item = ({ title, to, icon: Icon, activeIcon: ActiveIcon, collapsed, exact = false, onNavigate }) => (
  <Tooltip.Root openDelay={0} positioning={{ placement: "right-end" }} disabled={!collapsed}>
    <Tooltip.Trigger asChild>
      <Box w="full">
        <NavLink to={to} end={exact} onClick={onNavigate}>
          {({ isActive }) => (
            <Flex
              align="center"
              gap="3"
              px={collapsed ? "2.5" : "3"}
              py="2.5"
              rounded="xl"
              justify={collapsed ? "center" : "start"}
              bg={isActive ? "dashboard.accent.solid" : "transparent"}
              color={isActive ? "dashboard.fg.onAccent" : "dashboard.fg.muted"}
              fontWeight={isActive ? "700" : "500"}
              fontFamily={isActive ? "display" : "body"}
              fontSize="sm"
              boxShadow={isActive ? "0 3px 0 color-mix(in srgb, currentColor 30%, transparent)" : "none"}
              transition="background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease"
              _hover={{
                bg: isActive ? "dashboard.accent.solid" : "dashboard.bg.surfaceMuted",
                color: isActive ? "dashboard.fg.onAccent" : "dashboard.fg.default",
              }}
            >
              {isActive && ActiveIcon
                ? <ActiveIcon size="1.2rem" />
                : <Icon size="1.2rem" />}
              {!collapsed && <Box flex="1">{title}</Box>}
            </Flex>
          )}
        </NavLink>
      </Box>
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content>{title}</Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
);

function getSectionsForPortal(portal) {
  if (portal === "camp") {
    return [
      {
        title: "General",
        items: [{ title: "Dashboard", to: "", icon: MdOutlineHome, activeIcon: MdHome, exact: true }],
      },
      {
        title: "Datos",
        items: [
          { title: "Socios", to: "socios", icon: MdOutlinePeople, activeIcon: MdPeople },
          { title: "Instructores", to: "instructores", icon: MdOutlinePerson, activeIcon: MdPerson },
        ],
      },
      {
        title: "Administrativo",
        items: [{ title: "Accesos", to: "accesos", icon: MdOutlineKey, activeIcon: MdKey }],
      },
    ];
  }

  return [
    {
      title: "General",
      items: [{ title: "Dashboard", to: "", icon: MdOutlineHome, activeIcon: MdHome, exact: true }],
    },
    {
      title: "Datos",
      items: [
        { title: "Actividades", to: "actividades", icon: MdOutlineRocketLaunch, activeIcon: MdRocketLaunch },
        { title: "Socios", to: "socios", icon: MdOutlinePeople, activeIcon: MdPeople },
        { title: "Instructores", to: "instructores", icon: MdOutlinePerson, activeIcon: MdPerson },
      ],
    },
    {
      title: "Adicional",
      items: [
        { title: "Fotos", to: "fotos", icon: MdOutlineImage, activeIcon: MdImage },
        { title: "Lugares", to: "lugares", icon: MdOutlinePark, activeIcon: MdPark },
        { title: "Categorias", to: "categorias", icon: MdOutlineCategory, activeIcon: MdCategory },
        { title: "Tags", to: "tags", icon: MdOutlineLocalOffer, activeIcon: MdLocalOffer },
      ],
    },
    {
      title: "Administrativo",
      items: [{ title: "Accesos", to: "accesos", icon: MdOutlineKey, activeIcon: MdKey }],
    },
  ];
}

const Sidebar = ({ onNavigate }) => {
  const s = useDashboardSettings();
  const [collapsed, setCollapsed] = useState(false);

  const portal = s.portal || "club";
  const basePath = getDashboardPath(portal);
  const sections = useMemo(() => getSectionsForPortal(portal), [portal]);

  return (
    <Box
      as="aside"
      w={["full", collapsed ? "72px" : "260px"]}
      h="100vh"
      bg="dashboard.bg.panel"
      borderRightWidth="1px"
      borderRightColor="dashboard.border.muted"
      transition="width 0.25s ease"
      position="sticky"
      top="0"
      flexShrink={0}
      zIndex="10"
    >
      <Flex direction="column" h="100%">

        {/* Header */}
        <Flex
          align="center"
          justify={collapsed ? "center" : "space-between"}
          px={collapsed ? "2" : "4"}
          flexShrink={0}
          borderBottomWidth="1px"
          borderBottomColor="dashboard.border.muted"
          h="64px"
        >
          {!collapsed && (
            <Link to="/">
              <ArawaksLogo h="9" />
            </Link>
          )}
          <IconButton
            variant="ghost"
            color="dashboard.fg.subtle"
            onClick={() => setCollapsed((c) => !c)}
            rounded="lg"
            size="sm"
            display={["none", "flex"]}
            aria-label="Toggle sidebar"
            _hover={{ bg: "dashboard.bg.surfaceMuted", color: "dashboard.fg.default" }}
          >
            <MdOutlineMenu />
          </IconButton>
        </Flex>

        {/* Nav */}
        <Box flex="1" overflowY="auto" py="4" px={collapsed ? "2" : "3"}>
          <VStack gap="0" align="stretch">
            {sections.map((section, si) => (
              <React.Fragment key={section.title}>
                {si > 0 && (
                  <Box
                    h="1px"
                    bg="dashboard.border.muted"
                    mx="2"
                    my="3"
                  />
                )}
                {!collapsed && (
                  <Text sx={labelStyle} color="dashboard.fg.subtle" px="3" pb="1.5">
                    {section.title}
                  </Text>
                )}
                <VStack gap="0.5" align="stretch">
                  {section.items.map((item) => (
                    <Item
                      key={item.title}
                      title={item.title}
                      to={item.to ? `${basePath}/${item.to}` : basePath}
                      icon={item.icon}
                      activeIcon={item.activeIcon}
                      collapsed={collapsed}
                      exact={item.exact}
                      onNavigate={onNavigate}
                    />
                  ))}
                </VStack>
              </React.Fragment>
            ))}
          </VStack>
        </Box>

      </Flex>
    </Box>
  );
};

export default Sidebar;
